// Custom hook for ClassificationStep state and logic

import { useState, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../../setup/redux/hooks";
import type { RootState } from "../../../../setup/redux/store";
import type { CategoryItem } from "src/shared/forms/components/CategoryBrowser/CategoryBrowser";
import type { MarketplaceType } from "src/app/models/MarketplaceCategory";
import type { AccountCategoryAttribute } from "../../category/types/AccountCategory";
import { useAccountCategoryAttributes } from "../../category/hooks/useAccountCategories";
import { fetchAccessibleAccounts } from "../../account/redux/accountSlice";
import AccountCategoryService from "../../category/services/accountCategoryService";
import fileUploadService from "../services/fileUploadService";
import { AVAILABLE_BRANDS } from "./classificationConstants";
import { toCategoryItem, getFallbackMarketplace, filterBrands } from "./classificationHelpers";

// Interface for classification data (matching ClassificationStep)
export interface ClassificationData {
  // Categorization & Classification
  categoryId?: string;
  categoryPath?: CategoryItem[];
  brandId?: string;
  brandName?: string;

  // Dynamic Product Specification Attributes
  categoryAttributes?: { [attributeName: string]: any };

  // Qualification
  licenseNumber?: string;
  licenseFile?: File | null;

  // File upload integration fields
  licenseFileId?: string;
  licenseFileUrl?: string;
  licenseUploadStatus?: 'idle' | 'uploading' | 'success' | 'error';
  licenseUploadError?: string;
}

export interface UseClassificationStepOptions {
  classificationData: ClassificationData;
  onChange: (data: ClassificationData) => void;
}

export interface UseClassificationStepReturn {
  // State
  showBrandDropdown: boolean;
  setShowBrandDropdown: (show: boolean) => void;
  brandSearchQuery: string;
  setBrandSearchQuery: (query: string) => void;
  showCategoryModal: boolean;
  setShowCategoryModal: (show: boolean) => void;
  rootCategories: CategoryItem[];
  rootLoading: boolean;
  rootError: string | null;

  // Computed values
  marketplace: MarketplaceType;
  leafCategoryId: string | undefined;
  filteredBrands: typeof AVAILABLE_BRANDS;

  // Attribute hook values
  categoryAttributes: AccountCategoryAttribute[];
  attributesLoading: boolean;
  attributesError: string | null;
  getSortedAttributes: () => AccountCategoryAttribute[];

  // Handlers
  handleFieldChange: (field: keyof ClassificationData, value: any) => void;
  handleBrandSelect: (brand: { id: string; name: string }) => void;
  loadChildren: (parentId: string) => Promise<CategoryItem[]>;
  handleCategorySelection: (categoryPath: CategoryItem[]) => void;
  handleLicenseFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  removeLicenseFile: () => void;
  retryLicenseUpload: () => Promise<void>;
  openCategoryModal: () => void;
  updateCategoryAttributes: (updates: Partial<ClassificationData['categoryAttributes']>) => void;
}

export function useClassificationStep({
  classificationData,
  onChange,
}: UseClassificationStepOptions): UseClassificationStepReturn {
  const dispatch = useAppDispatch();

  // State for brand dropdown and search
  const [showBrandDropdown, setShowBrandDropdown] = useState(false);
  const [brandSearchQuery, setBrandSearchQuery] = useState("");

  // State for category modal
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  // Root categories state (lazy loaded)
  const [rootCategories, setRootCategories] = useState<CategoryItem[]>([]);
  const [rootLoading, setRootLoading] = useState(false);
  const [rootError, setRootError] = useState<string | null>(null);

  // Get accounts from Redux for marketplace fallback logic
  const accounts = useSelector((state: RootState) => state.Account?.accounts || []);

  // Fetch accessible accounts on hook mount
  useEffect(() => {
    dispatch(fetchAccessibleAccounts({ useCache: true }) as any);
  }, [dispatch]);

  // Marketplace is always "merchant" first
  const marketplace: MarketplaceType = "merchant" as MarketplaceType;

  // Get leaf category ID for attributes
  const leafCategoryId = classificationData.categoryPath && classificationData.categoryPath.length > 0
    ? classificationData.categoryPath[classificationData.categoryPath.length - 1].id
    : classificationData.categoryId;

  // Use account category attributes hook
  const {
    attributes: categoryAttributes,
    loading: attributesLoading,
    error: attributesError,
    fetchAttributes,
    getSortedAttributes,
  } = useAccountCategoryAttributes(leafCategoryId);

  // Load root categories with fallback logic (merchant first, then account marketplace)
  useEffect(() => {
    const loadRootCategories = async () => {
      setRootLoading(true);
      setRootError(null);

      try {
        const service = AccountCategoryService.getInstance();

        // Step 1: Try "merchant" marketplace first
        try {
          console.log('Attempting to load categories with "merchant" marketplace...');
          const merchantResponse = await service.getRootCategories("merchant");

          if (merchantResponse.data.categories && merchantResponse.data.categories.length > 0) {
            console.log('Successfully loaded categories with "merchant" marketplace');
            setRootCategories(merchantResponse.data.categories.map(toCategoryItem));
            return; // Success with "merchant", no need to fallback
          } else {
            console.log('No categories found with "merchant" marketplace, trying fallback...');
          }
        } catch (merchantError) {
          console.log('Failed to load categories with "merchant" marketplace, trying fallback...', merchantError);
        }

        // Step 2: Fallback to first sorted account marketplace
        const fallbackMarketplace = getFallbackMarketplace(accounts);
        if (fallbackMarketplace && ["shopee", "lazada", "tiktok", "tiktokshop"].includes(fallbackMarketplace)) {
          console.log(`Falling back to "${fallbackMarketplace}" marketplace from accessible accounts...`);
          const fallbackResponse = await service.getRootCategories(fallbackMarketplace);
          setRootCategories(fallbackResponse.data.categories.map(toCategoryItem));
        } else {
          throw new Error("No valid marketplace found for category loading");
        }
      } catch (err) {
        console.error("Failed to load categories:", err);
        setRootError("Failed to load categories");
        setRootCategories([]);
      } finally {
        setRootLoading(false);
      }
    };

    // Only load if we have accounts or if we want to try "merchant" anyway
    loadRootCategories();
  }, [accounts]); // Depend on accounts instead of marketplace

  // Fetch attributes when category changes (with same fallback logic)
  useEffect(() => {
    const fetchCategoryAttributes = async () => {
      if (!leafCategoryId) return;

      console.log('Fetching attributes for category:', leafCategoryId);

      // Step 1: Try "merchant" marketplace first
      console.log('Attempting to fetch attributes with "merchant" marketplace...');
      try {
        const merchantResult = await fetchAttributes("merchant", leafCategoryId);

        // Check if the Redux action was fulfilled
        if (merchantResult.type.endsWith('/fulfilled')) {
          console.log('Successfully fetched attributes with "merchant" marketplace');
          return; // Success with "merchant"
        } else if (merchantResult.type.endsWith('/rejected')) {
          console.log('Failed to fetch attributes with "merchant" marketplace, trying fallback...', merchantResult.error);
          throw new Error(merchantResult.error?.message || 'Merchant marketplace rejected');
        }
      } catch (merchantError) {
        console.log('Failed to fetch attributes with "merchant" marketplace, trying fallback...', merchantError);

        // Step 2: Fallback to first sorted account marketplace
        const fallbackMarketplace = getFallbackMarketplace(accounts);
        if (fallbackMarketplace && ["shopee", "lazada", "tiktok", "tiktokshop"].includes(fallbackMarketplace)) {
          console.log(`Falling back to "${fallbackMarketplace}" marketplace for attributes...`);
          try {
            const fallbackResult = await fetchAttributes(fallbackMarketplace, leafCategoryId);
            if (fallbackResult.type.endsWith('/fulfilled')) {
              console.log(`Successfully fetched attributes with "${fallbackMarketplace}" marketplace`);
            } else {
              console.error(`Failed to fetch attributes with fallback marketplace "${fallbackMarketplace}"`, fallbackResult.error);
            }
          } catch (fallbackError) {
            console.error(`Failed to fetch attributes with fallback marketplace "${fallbackMarketplace}":`, fallbackError);
          }
        } else {
          console.error('No valid fallback marketplace found for attribute loading');
        }
      }
    };

    fetchCategoryAttributes();
  }, [leafCategoryId, accounts, fetchAttributes]);

  // Filtered brands for search
  const filteredBrands = filterBrands(AVAILABLE_BRANDS, brandSearchQuery);

  // Update category attributes
  const updateCategoryAttributes = useCallback((attributeUpdates: Partial<ClassificationData['categoryAttributes']>) => {
    onChange({
      ...classificationData,
      categoryAttributes: {
        ...(classificationData.categoryAttributes || {}),
        ...attributeUpdates,
      },
    });
  }, [classificationData, onChange]);

  // Handle field changes
  const handleFieldChange = useCallback(
    (field: keyof ClassificationData, value: any) => {
      onChange({
        ...classificationData,
        [field]: value,
      });
    },
    [classificationData, onChange]
  );

  // Handle brand selection
  const handleBrandSelect = useCallback(
    (brand: { id: string; name: string }) => {
      onChange({
        ...classificationData,
        brandId: brand.id,
        brandName: brand.name,
      });
      setShowBrandDropdown(false);
      setBrandSearchQuery("");
    },
    [classificationData, onChange]
  );

  // Load children for a category (callback for CategoryBrowser) with fallback logic
  const loadChildren = useCallback(
    async (parentId: string): Promise<CategoryItem[]> => {
      try {
        const service = AccountCategoryService.getInstance();

        // Step 1: Try "merchant" marketplace first
        try {
          const merchantResponse = await service.getChildCategories("merchant", parentId);
          if (merchantResponse.data.categories && merchantResponse.data.categories.length > 0) {
            return merchantResponse.data.categories.map(toCategoryItem);
          }
        } catch (merchantError) {
          console.log('Failed to load child categories with "merchant" marketplace, trying fallback...');
        }

        // Step 2: Fallback to first sorted account marketplace
        const fallbackMarketplace = getFallbackMarketplace(accounts);
        if (fallbackMarketplace) {
          const fallbackResponse = await service.getChildCategories(fallbackMarketplace, parentId);
          return fallbackResponse.data.categories.map(toCategoryItem);
        }

        return [];
      } catch (err) {
        console.error("Failed to load child categories:", err);
        return [];
      }
    },
    [accounts]
  );

  // Handle category selection from CategoryBrowser
  const handleCategorySelection = useCallback(
    (categoryPath: CategoryItem[]) => {
      if (categoryPath.length === 0) {
        onChange({
          ...classificationData,
          categoryId: undefined,
          categoryPath: undefined,
          categoryAttributes: {}
        });
        setShowCategoryModal(false);
        return;
      }

      // Get the last (leaf) category in the path
      const leafCategory = categoryPath[categoryPath.length - 1];
      onChange({
        ...classificationData,
        categoryId: leafCategory.id,
        categoryPath: categoryPath,
        categoryAttributes: {} // Reset attributes when category changes
      });
      setShowCategoryModal(false);
    },
    [classificationData, onChange]
  );

  // Handle file selection for license document
  const handleLicenseFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] || null;

      if (!file) return;

      // Store file for preview and set uploading status
      onChange({
        ...classificationData,
        licenseFile: file,
        licenseUploadStatus: 'uploading',
        licenseUploadError: undefined
      });

      try {
        // Upload to server
        const result = await fileUploadService.uploadFile(file, 'certificate');

        // Update with server file_id and success status
        onChange({
          ...classificationData,
          licenseFile: file,
          licenseFileId: result.file_id,
          licenseFileUrl: result.file_url,
          licenseUploadStatus: 'success',
          licenseUploadError: undefined
        });
      } catch (error: any) {
        console.error('License file upload failed:', error);

        // Update with error status
        onChange({
          ...classificationData,
          licenseFile: file,
          licenseUploadStatus: 'error',
          licenseUploadError: error?.message || 'Failed to upload file'
        });
      }
    },
    [classificationData, onChange]
  );

  // Remove license file
  const removeLicenseFile = useCallback(() => {
    onChange({
      ...classificationData,
      licenseFile: null,
      licenseFileId: undefined,
      licenseFileUrl: undefined,
      licenseUploadStatus: 'idle',
      licenseUploadError: undefined
    });
    // Clear the input value
    const fileInput = document.querySelector(
      'input[type="file"][accept*=".pdf"]'
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  }, [classificationData, onChange]);

  // Retry failed license file upload
  const retryLicenseUpload = useCallback(async () => {
    if (!classificationData.licenseFile) return;

    // Set uploading status
    onChange({
      ...classificationData,
      licenseUploadStatus: 'uploading',
      licenseUploadError: undefined
    });

    try {
      // Upload to server
      const result = await fileUploadService.uploadFile(classificationData.licenseFile, 'certificate');

      // Update with server file_id and success status
      onChange({
        ...classificationData,
        licenseFileId: result.file_id,
        licenseFileUrl: result.file_url,
        licenseUploadStatus: 'success',
        licenseUploadError: undefined
      });
    } catch (error: any) {
      console.error('License file upload retry failed:', error);

      // Update with error status
      onChange({
        ...classificationData,
        licenseUploadStatus: 'error',
        licenseUploadError: error?.message || 'Failed to upload file'
      });
    }
  }, [classificationData, onChange]);

  // Handle opening category modal
  const openCategoryModal = useCallback(() => {
    setShowCategoryModal(true);
  }, []);

  return {
    // State
    showBrandDropdown,
    setShowBrandDropdown,
    brandSearchQuery,
    setBrandSearchQuery,
    showCategoryModal,
    setShowCategoryModal,
    rootCategories,
    rootLoading,
    rootError,

    // Computed values
    marketplace,
    leafCategoryId,
    filteredBrands,

    // Attribute hook values
    categoryAttributes,
    attributesLoading,
    attributesError,
    getSortedAttributes,

    // Handlers
    handleFieldChange,
    handleBrandSelect,
    loadChildren,
    handleCategorySelection,
    handleLicenseFileChange,
    removeLicenseFile,
    retryLicenseUpload,
    openCategoryModal,
    updateCategoryAttributes,
  };
}
